/**
 * Desktop dropdown submenu hover handling
 *
 * Initializes hover interactions for desktop navigation menus with submenus.
 * Shows/hides dropdowns on mouseenter/mouseleave with a slight delay before hiding.
 *
 * @since 1.0.0
 */
export function initDesktopMenu() {
	const desktopMenuItems = document.querySelectorAll('.menu-primary-list ul .menu-item-has-children');
	let hideDropdownTimeout;

	desktopMenuItems.forEach(function (item) {
		const submenu = item.querySelector('.sub-menu');
		if (!submenu) {
			return;
		}

		item.addEventListener('mouseenter', function () {
			clearTimeout(hideDropdownTimeout);
			submenu.classList.add('is-visible');
		});

		item.addEventListener('mouseleave', function () {
			hideDropdownTimeout = setTimeout(function () {
				submenu.classList.remove('is-visible');
			}, 150);
		});

		submenu.addEventListener('mouseenter', function () {
			clearTimeout(hideDropdownTimeout);
			submenu.classList.add('is-visible');
		});

		submenu.addEventListener('mouseleave', function () {
			hideDropdownTimeout = setTimeout(function () {
				submenu.classList.remove('is-visible');
			}, 150);
		});
	});
}

/**
 * Mobile menu toggle functionality
 *
 * Initializes mobile navigation menu with support for:
 * - Toggle open/close of main mobile menu
 * - Mobile submenu dropdowns with chevron button
 * - Closing menu when clicking menu items
 *
 * @since 1.0.0
 */
export function initMobileMenu() {
	const menuTrigger = document.querySelector('.menu-primary-trigger');
	const mobileMenu = document.querySelector('.menu-primary-mobile');
	const mobileClose = document.querySelector('.menu-primary-mobile-close');

	if (!menuTrigger || !mobileMenu) {
		return;
	}

	const toggleMenu = function (isOpen) {
		if (isOpen) {
			mobileMenu.classList.add('is-open');
			mobileMenu.setAttribute('aria-hidden', 'false');
			menuTrigger.setAttribute('aria-expanded', 'true');
			document.body.style.overflow = 'hidden';
		} else {
			mobileMenu.classList.remove('is-open');
			mobileMenu.setAttribute('aria-hidden', 'true');
			menuTrigger.setAttribute('aria-expanded', 'false');
			document.body.style.overflow = '';
		}
	};

	menuTrigger.addEventListener('click', function (e) {
		e.preventDefault();
		toggleMenu(!mobileMenu.classList.contains('is-open'));
	});

	if (mobileClose) {
		mobileClose.addEventListener('click', function (e) {
			e.preventDefault();
			toggleMenu(false);
		});
	}

	// Handle mobile submenu dropdown
	const menuItemsWithChildren = mobileMenu.querySelectorAll('.menu-item-has-children');
	menuItemsWithChildren.forEach(function (item) {
		const link = item.querySelector('a');
		const submenu = item.querySelector('.sub-menu');

		if (!link || !submenu) {
			return;
		}

		// Wrap link text in span (except the ::after pseudo element)
		const linkText = Array.from(link.childNodes)
			.filter(node => node.nodeType === Node.TEXT_NODE)
			.map(node => node.textContent.trim())
			.join('');

		if (linkText && !link.querySelector('.menu-item-text')) {
			// Clear text nodes
			Array.from(link.childNodes).forEach(node => {
				if (node.nodeType === Node.TEXT_NODE) {
					node.remove();
				}
			});

			// Create new span for text and prepend
			const textSpan = document.createElement('span');
			textSpan.className = 'menu-item-text';
			textSpan.textContent = linkText;
			link.insertBefore(textSpan, link.firstChild);
		}

		// Create dedicated chevron button
		let chevronButton = link.querySelector('.menu-item-chevron');
		if (!chevronButton) {
			chevronButton = document.createElement('button');
			chevronButton.className = 'menu-item-chevron';
			chevronButton.type = 'button';
			chevronButton.setAttribute('aria-expanded', 'false');
			chevronButton.setAttribute('aria-label', `Toggle submenu for ${linkText}`);
			link.appendChild(chevronButton);
		}

		// Allow text link to navigate normally
		const textSpan = link.querySelector('.menu-item-text');
		if (textSpan) {
			textSpan.addEventListener('click', function (e) {
				e.stopPropagation();
				if (item.classList.contains('is-open')) {
					e.preventDefault();
				}
				// Otherwise allow natural navigation
			});
		}

		// Only chevron toggles submenu
		chevronButton.addEventListener('click', function (e) {
			e.preventDefault();
			e.stopPropagation();

			if (item.classList.contains('is-open')) {
				item.classList.remove('is-open');
				chevronButton.setAttribute('aria-expanded', 'false');
			} else {
				// Close other open submenus
				menuItemsWithChildren.forEach(function (sibling) {
					if (sibling !== item) {
						sibling.classList.remove('is-open');
						const siblingChevron = sibling.querySelector('.menu-item-chevron');
						if (siblingChevron) {
							siblingChevron.setAttribute('aria-expanded', 'false');
						}
					}
				});
				item.classList.add('is-open');
				chevronButton.setAttribute('aria-expanded', 'true');
			}
		});
	});

	// Close menu when clicking a link (but not parent items)
	const mobileMenuLinks = mobileMenu.querySelectorAll('a');
	mobileMenuLinks.forEach(function (link) {
		// Check if this link is not triggering a submenu
		const parentItem = link.closest('.menu-item');
		if (!parentItem || !parentItem.classList.contains('menu-item-has-children')) {
			link.addEventListener('click', function () {
				toggleMenu(false);
				// Close all submenus
				menuItemsWithChildren.forEach(function (item) {
					item.classList.remove('is-open');
				});
			});
		}
	});

	// Close submenus when closing main menu
	const closeMenuObserver = new MutationObserver(function () {
		if (!mobileMenu.classList.contains('is-open')) {
			menuItemsWithChildren.forEach(function (item) {
				item.classList.remove('is-open');
			});
		}
	});
	closeMenuObserver.observe(mobileMenu, { attributes: true, attributeFilter: ['class'] });
}
